import type { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronsDown } from "@fortawesome/pro-solid-svg-icons/faChevronsDown";
import { faChevronsUp } from "@fortawesome/pro-solid-svg-icons/faChevronsUp";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { faCircleDashed } from "@fortawesome/pro-solid-svg-icons/faCircleDashed";
import { faDash } from "@fortawesome/pro-solid-svg-icons/faDash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ForwardedRef, forwardRef } from "react";
import { type ClassNameValue } from "tailwind-merge";

import {
  type AskedCompetencyFit,
  NotAsked,
  UpwageBadge,
} from "@/components/upwage-badge";
import { useGetInternalOnlyRender } from "@/hooks/use-internal-only-render";
import { CompetencyFit } from "@/types/screeners";
import type { Nullable, SingleOrArray } from "@/types/utils";
import { renderRawScore } from "@/utils/ui";

interface CompetencyBadgeProps {
  className?: SingleOrArray<ClassNameValue>;
  isOverallFit?: boolean;
  rawScore?: Nullable<number>;
  score: CompetencyFit;
}

const BADGE_ICONS: Record<AskedCompetencyFit, IconDefinition> = {
  [CompetencyFit.VeryHigh]: faChevronsUp,
  [CompetencyFit.High]: faChevronUp,
  [CompetencyFit.Medium]: faDash,
  [CompetencyFit.Low]: faChevronDown,
  [CompetencyFit.VeryLow]: faChevronsDown,
  [CompetencyFit.Absent]: faCircleDashed,
};

export const CompetencyBadge = forwardRef(
  (
    { className, isOverallFit = false, rawScore, score }: CompetencyBadgeProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const internalOnlyRender = useGetInternalOnlyRender();
    const rawScoreUI = renderRawScore(internalOnlyRender, rawScore);

    if (score === CompetencyFit.NotAsked) {
      return <NotAsked />;
    }

    return (
      <UpwageBadge
        className={className}
        colorVariant={score}
        filled={isOverallFit}
        leftSection={<FontAwesomeIcon icon={BADGE_ICONS[score]} size="sm" />}
        ref={ref}
      >
        {score}
        {rawScoreUI}
      </UpwageBadge>
    );
  },
);
